/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/shared/types';

export const thTH: LocalizationResource = {
  locale: 'th-TH',
  apiKeys: {
    action__add: 'เพิ่มคีย์ใหม่',
    action__search: 'ค้นหาคีย์',
    copySecret: {
      formButtonPrimary__copyAndClose: 'คัดลอกและปิด',
      formHint: 'ด้วยเหตุผลด้านความปลอดภัย เราจะไม่อนุญาตให้คุณดูอีกครั้งในภายหลัง',
      formTitle: 'คัดลอกคีย์ API "{{name}}" ของคุณตอนนี้',
    },
    createdAndExpirationStatus__expiresOn:
      "สร้างเมื่อ {{ createdDate | shortDate('th-TH') }} • หมดอายุ {{ expiresDate | longDate('th-TH') }}",
    createdAndExpirationStatus__never: "สร้างเมื่อ {{ createdDate | shortDate('th-TH') }} • ไม่มีวันหมดอายุ",
    detailsTitle__emptyRow: 'ไม่พบคีย์ API',
    formButtonPrimary__add: 'สร้างคีย์',
    formFieldCaption__expiration__expiresOn: 'หมดอายุ {{ date }}',
    formFieldCaption__expiration__never: 'คีย์นี้จะไม่มีวันหมดอายุ',
    formFieldOption__expiration__180d: '180 วัน',
    formFieldOption__expiration__1d: '1 วัน',
    formFieldOption__expiration__1y: '1 ปี',
    formFieldOption__expiration__30d: '30 วัน',
    formFieldOption__expiration__60d: '60 วัน',
    formFieldOption__expiration__7d: '7 วัน',
    formFieldOption__expiration__90d: '90 วัน',
    formFieldOption__expiration__never: 'ไม่มีวันหมดอายุ',
    formHint: 'ระบุชื่อเพื่อสร้างคีย์ใหม่ คุณสามารถเพิกถอนได้ตลอดเวลา',
    formTitle: 'เพิ่มคีย์ API ใหม่',
    lastUsed__days: '{{days}} วันที่แล้ว',
    lastUsed__hours: '{{hours}} ชั่วโมงที่แล้ว',
    lastUsed__minutes: '{{minutes}} นาทีที่แล้ว',
    lastUsed__months: '{{months}} เดือนที่แล้ว',
    lastUsed__seconds: '{{seconds}} วินาทีที่แล้ว',
    lastUsed__years: '{{years}} ปีที่แล้ว',
    menuAction__revoke: 'เพิกถอนคีย์',
    revokeConfirmation: {
      confirmationText: 'เพิกถอน',
      formButtonPrimary__revoke: 'เพิกถอนคีย์',
      formHint: 'คุณแน่ใจหรือไม่ว่าต้องการลบคีย์ลับนี้?',
      formTitle: 'เพิกถอนคีย์ลับ "{{apiKeyName}}"?',
      inputLabel: undefined,
    },
    tableHeader__actions: undefined,
    tableHeader__lastUsed: undefined,
    tableHeader__name: undefined,
  },
  backButton: 'ย้อนกลับ',
  badge__activePlan: 'ใช้งานอยู่',
  badge__banned: undefined,
  badge__canceledEndsAt: "ถูกยกเลิก • สิ้นสุด {{ date | shortDate('th-TH') }}",
  badge__currentPlan: 'แผนปัจจุบัน',
  badge__default: 'ค่าเริ่มต้น',
  badge__endsAt: "สิ้นสุด {{ date | shortDate('th-TH') }}",
  badge__expired: 'หมดอายุ',
  badge__freeTrial: 'ทดลองใช้ฟรี',
  badge__otherImpersonatorDevice: 'อุปกรณ์แอบอ้างอื่น',
  badge__pastDueAt: "เกินกำหนด {{ date | shortDate('th-TH') }}",
  badge__pastDuePlan: 'เกินกำหนด',
  badge__primary: 'หลัก',
  badge__renewsAt: "ต่ออายุ {{ date | shortDate('th-TH') }}",
  badge__requiresAction: 'ต้องดำเนินการ',
  badge__startsAt: "เริ่ม {{ date | shortDate('th-TH') }}",
  badge__thisDevice: 'อุปกรณ์นี้',
  badge__trialEndsAt: "ทดลองใช้สิ้นสุด {{ date | shortDate('th-TH') }}",
  badge__unverified: 'ยังไม่ได้ยืนยัน',
  badge__upcomingPlan: 'แผนที่จะมา',
  badge__userDevice: 'อุปกรณ์ผู้ใช้',
  badge__you: 'คุณ',
  billing: {
    accountCredit: undefined,
    addPaymentMethod__label: 'เพิ่มวิธีชำระเงิน',
    alwaysFree: 'ฟรีตลอดไป',
    annually: 'รายปี',
    availableFeatures: 'ฟีเจอร์ที่มี',
    billedAnnually: 'เรียกเก็บรายปี',
    billedAnnuallyOnly: undefined,
    billedMonthly: undefined,
    billedMonthlyOnly: 'เรียกเก็บรายเดือนเท่านั้น',
    cancelFreeTrial: 'ยกเลิกทดลองใช้ฟรี',
    cancelFreeTrialAccessUntil:
      "การทดลองใช้จะยังคงใช้งานได้จนถึง {{ date | longDate('th-TH') }} หลังจากนั้นคุณจะเสียสิทธิ์เข้าถึงฟีเจอร์การทดลองใช้ คุณจะไม่ถูกเรียกเก็บเงิน",
    cancelFreeTrialTitle: 'ยกเลิกการทดลองใช้ฟรีสำหรับแผน {{plan}}?',
    cancelSubscription: 'ยกเลิกการสมัครสมาชิก',
    cancelSubscriptionAccessUntil:
      "คุณสามารถใช้ฟีเจอร์ '{{plan}}' ต่อไปได้จนถึง {{ date | longDate('th-TH') }} หลังจากนั้นคุณจะไม่สามารถเข้าถึงได้อีก",
    cancelSubscriptionNoCharge: 'คุณจะไม่ถูกเรียกเก็บเงินสำหรับการสมัครสมาชิกนี้',
    cancelSubscriptionPastDue: undefined,
    cancelSubscriptionTitle: 'ยกเลิกการสมัครสมาชิก {{plan}}?',
    cannotSubscribeMonthly: 'คุณไม่สามารถสมัครแผนนี้โดยการชำระรายเดือน หากต้องการสมัครแผนนี้ คุณต้องเลือกชำระรายปี',
    cannotSubscribeUnrecoverable: 'คุณไม่สามารถสมัครแผนนี้ได้ การสมัครสมาชิกปัจจุบันของคุณมีราคาแพงกว่าแผนนี้',
    checkout: {
      description__paymentSuccessful: 'การชำระเงินของคุณสำเร็จ',
      description__subscriptionSuccessful: 'การสมัครสมาชิกใหม่ของคุณพร้อมแล้ว',
      downgradeNotice:
        'คุณจะยังคงใช้การสมัครสมาชิกปัจจุบันและฟีเจอร์ของมันจนจบรอบบิล จากนั้นคุณจะถูกเปลี่ยนไปใช้การสมัครสมาชิกนี้',
      emailForm: {
        subtitle: 'ก่อนที่คุณจะสามารถทำการซื้อให้เสร็จสิ้น คุณต้องเพิ่มที่อยู่อีเมลสำหรับส่งใบเสร็จ',
        title: 'เพิ่มที่อยู่อีเมล',
      },
      lineItems: {
        title__freeTrialEndsAt: 'ทดลองใช้สิ้นสุดวันที่',
        title__paymentMethod: 'วิธีชำระเงิน',
        title__statementId: 'รหัสใบแจ้งหนี้',
        title__subscriptionBegins: 'การสมัครสมาชิกเริ่มต้น',
        title__totalPaid: 'ยอดรวมที่ชำระ',
      },
      pastDueNotice: 'การสมัครสมาชิกก่อนหน้าของคุณเกินกำหนดและไม่มีการชำระเงิน',
      perMonth: 'ต่อเดือน',
      title: 'ชำระเงิน',
      title__paymentSuccessful: 'ชำระเงินสำเร็จ!',
      title__subscriptionSuccessful: 'สำเร็จ!',
      title__trialSuccess: 'เริ่มทดลองใช้สำเร็จ!',
      totalDueAfterTrial: 'ยอดรวมที่ต้องชำระหลังจากทดลองใช้สิ้นสุดใน {{days}} วัน',
      totalDuePerPeriod: undefined,
    },
    credit: 'เครดิต',
    creditRemainder: 'เครดิตสำหรับส่วนที่เหลือของการสมัครสมาชิกปัจจุบันของคุณ',
    defaultFreePlanActive: 'คุณกำลังใช้แผนฟรีอยู่',
    free: 'ฟรี',
    getStarted: 'เริ่มต้นใช้งาน',
    highlightedPlanBadge: 'ยอดนิยม',
    keepFreeTrial: 'ใช้ทดลองใช้ฟรีต่อ',
    keepSubscription: 'ใช้การสมัครสมาชิกต่อ',
    manage: 'จัดการ',
    manageSubscription: 'จัดการการสมัครสมาชิก',
    month: 'เดือน',
    monthAbbreviation: undefined,
    monthPerUnit: undefined,
    monthly: 'รายเดือน',
    pastDue: 'เกินกำหนด',
    pay: 'ชำระ {{amount}}',
    payerCreditRemainder: undefined,
    paymentMethod: {
      applePayDescription: {
        annual: 'ชำระรายปี',
        monthly: 'ชำระรายเดือน',
      },
      dev: {
        anyNumbers: 'ตัวเลขใดก็ได้',
        cardNumber: 'หมายเลขบัตร',
        cvcZip: 'CVC, รหัสไปรษณีย์',
        developmentMode: 'โหมดการพัฒนา',
        expirationDate: 'วันที่หมดอายุ',
        testCardInfo: 'ข้อมูลบัตรทดสอบ',
      },
    },
    paymentMethods__label: 'วิธีการชำระเงิน',
    pricingTable: {
      billingCycle: 'รอบการเรียกเก็บเงิน',
      included: 'รวมอยู่',
      seatCost: {
        additionalSeats: undefined,
        freeUpToSeats: undefined,
        includedSeats: undefined,
        perSeat: undefined,
        tooltip: {
          additionalSeatsEach: undefined,
          firstSeatsIncludedInPlan: undefined,
          freeForUpToSeats: undefined,
        },
        unlimitedSeats: undefined,
        upToSeats: undefined,
      },
    },
    proratedDiscount: undefined,
    prorationCredit: undefined,
    reSubscribe: 'สมัครใหม่',
    seatBreakdownIncludedPlural: undefined,
    seatBreakdownIncludedSingular: undefined,
    seatBreakdownPlural: undefined,
    seatBreakdownSingular: undefined,
    seats: undefined,
    seatsWithLimit: undefined,
    seeAllFeatures: 'ดูฟีเจอร์ทั้งหมด',
    startFreeTrial: 'เริ่มทดลองใช้ฟรี',
    startFreeTrial__days: 'เริ่มทดลองใช้ฟรี {{days}} วัน',
    subscribe: 'สมัครสมาชิก',
    subscriptionDetails: {
      beginsOn: 'เริ่มต้นเมื่อ',
      currentBillingCycle: 'รอบการเรียกเก็บเงินปัจจุบัน',
      endsOn: 'สิ้นสุดเมื่อ',
      firstPaymentAmount: 'จำนวนเงินการชำระครั้งแรก',
      firstPaymentOn: 'ชำระครั้งแรกเมื่อ',
      nextPaymentAmount: 'จำนวนเงินการชำระครั้งถัดไป',
      nextPaymentOn: 'ชำระครั้งถัดไปเมื่อ',
      pastDueAt: 'เกินกำหนดเมื่อ',
      renewsAt: 'ต่ออายุเมื่อ',
      subscribedOn: 'สมัครสมาชิกเมื่อ',
      title: 'การสมัครสมาชิก',
      trialEndsOn: 'ทดลองใช้สิ้นสุดเมื่อ',
      trialStartedOn: 'เริ่มทดลองใช้เมื่อ',
    },
    subtotal: 'ยอดรวมย่อย',
    subtotalRenewal: undefined,
    switchPlan: 'เปลี่ยนไปใช้แผนนี้',
    switchToAnnual: 'เปลี่ยนเป็นรายปี',
    switchToAnnualWithAnnualPrice: 'เปลี่ยนเป็นรายปี {{price}} / ปี',
    switchToMonthly: 'เปลี่ยนเป็นรายเดือน',
    switchToMonthlyWithPrice: 'เปลี่ยนเป็นรายเดือน {{price}} / เดือน',
    totalDue: 'ยอดรวมที่ต้องชำระ',
    totalDuePerPeriod: undefined,
    totalDueToday: 'ยอดรวมที่ต้องชำระวันนี้',
    viewFeatures: 'ดูฟีเจอร์',
    viewPayment: 'ดูการชำระเงิน',
    year: 'ปี',
    yearAbbreviation: undefined,
    yearPerUnit: undefined,
  },
  configureSSO: {
    activate: {
      activateButton: undefined,
      activeSubtitle: undefined,
      activeTitle: undefined,
      doneButton: undefined,
      skipButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    changeProviderDialog: {
      cancelButton: undefined,
      confirmButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    configureStep: {
      activeConnectionWarning: {
        dismiss: undefined,
        title: undefined,
      },
      attributeMappingTable: {
        badges: {
          optional: undefined,
          required: undefined,
        },
      },
      samlCustom: {
        assignUsersStep: {
          headerSubtitle: undefined,
          paragraph: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attributeName: undefined,
              userAttribute: undefined,
            },
            rows: {
              email: {
                attributeName: undefined,
                userAttribute: undefined,
              },
              firstName: {
                attributeName: undefined,
                userAttribute: undefined,
              },
              lastName: {
                attributeName: undefined,
                userAttribute: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            paragraph: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
      samlGoogle: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              appAttribute: undefined,
              googleAttribute: undefined,
            },
            rows: {
              email: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              firstName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              lastName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        configureUserAccess: {
          assignUsersInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          headerSubtitle: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataFile: {
            description: undefined,
            fileUploaded: undefined,
            label: undefined,
            removeFile: undefined,
            replaceFile: undefined,
            uploadFile: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataFile: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          nameIdInstructions: {
            step1: undefined,
            step2: undefined,
          },
          paragraph: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          title: undefined,
        },
      },
      samlMicrosoft: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attribute: undefined,
              claimName: undefined,
              value: undefined,
            },
            copyClaimName: undefined,
            copyClaimNameCopied: undefined,
            rows: {
              email: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              firstName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              lastName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          step1: undefined,
          step2: undefined,
          title: undefined,
        },
        createAppStep: {
          assignUsersInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: {
              label: undefined,
              subSteps: {
                appName: undefined,
                create: undefined,
                nonGallery: undefined,
              },
            },
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          step1: undefined,
          step2: undefined,
          step3: undefined,
          step4: undefined,
          step5: undefined,
          step6: undefined,
          title: undefined,
        },
      },
      samlOkta: {
        assignUsersStep: {
          assignUsersInstructions: {
            paragraph: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
          },
          headerSubtitle: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              expression: undefined,
              name: undefined,
            },
            rows: {
              email: {
                expression: undefined,
                name: undefined,
              },
              firstName: {
                expression: undefined,
                name: undefined,
              },
              lastName: {
                expression: undefined,
                name: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        createAppStep: {
          completeSamlIntegrationInstructions: {
            step1: undefined,
            step2: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            serviceProviderFields: {
              acsUrl: {
                label: undefined,
              },
              spEntityId: {
                label: undefined,
              },
            },
            title: undefined,
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
    },
    missingManageEnterpriseConnectionsPermission: {
      subtitle: 'ติดต่อผู้ดูแลระบบขององค์กรของคุณเพื่ออัปเกรดสิทธิ์ของคุณ',
      title: 'คุณไม่มีสิทธิ์จัดการการลงชื่อเข้าใช้แบบครั้งเดียว (SSO)',
    },
    navbar: {
      title: 'กำหนดค่าการลงชื่อเข้าใช้แบบครั้งเดียว (SSO)',
    },
    organizationDomainsStep: {
      domainCard: {
        badge__expired: undefined,
        badge__unverified: 'ยังไม่ได้ยืนยัน',
        badge__verified: 'ยืนยันแล้ว',
        expiredAtLabel: undefined,
        expiredLabel: undefined,
        removeButtonTooltip__lastVerifiedDomain: undefined,
        removeButtonTooltip__lastVerifiedDomainActive: undefined,
        txtRecord: {
          hostLabel: 'โฮสต์ / ชื่อ',
          instructions: 'เพิ่มระเบียน TXT นี้ไปยังผู้ให้บริการ DNS ของคุณ เราจะยืนยันโดยอัตโนมัติเมื่อระเบียนทำงาน',
          typeLabel: 'ประเภท',
          valueLabel: 'ค่า',
        },
        verifiedAtLabel: "ยืนยันเมื่อ {{ date | shortDate('th-TH') }}",
        verifyAgainButton: undefined,
      },
      domainSuggestion: {
        formButtonPrimary__add: 'เพิ่ม {{domain}}',
        messageLabel: 'อีเมลของคุณใช้ {{domain}} คุณต้องการเพิ่มหรือไม่?',
      },
      formButtonPrimary__add: 'เพิ่ม',
      formFieldInputPlaceholder__domain: 'เพิ่มโดเมน',
      formFieldLabel__domain: 'โดเมน',
      removeDomainDialog: {
        cancelButton: undefined,
        removeButton: undefined,
        subtitle__active: undefined,
        subtitle__inactive: undefined,
        title: undefined,
      },
      subtitle: 'เพิ่มและยืนยันความเป็นเจ้าของโดเมนที่องค์กรของคุณใช้ในการลงชื่อเข้าใช้',
      title: 'เพิ่มโดเมน SSO',
    },
    resetConnectionDialog: {
      cancelButton: undefined,
      confirmationFieldLabel: undefined,
      confirmationFieldPlaceholder: undefined,
      resetButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    selectProviderStep: {
      saml: {
        customSaml: 'ผู้ให้บริการ SAML แบบกำหนดเอง',
        google: undefined,
        groupLabel: 'SAML',
        microsoft: undefined,
        okta: 'Okta Workforce',
      },
      subtitle: 'เลือกผู้ให้บริการที่คุณจะตั้งค่า SSO ให้',
      title: 'เลือกผู้ให้บริการ',
      warning: 'เมื่อเลือกผู้ให้บริการแล้วคุณไม่สามารถเปลี่ยนได้อีกจนกว่าการกำหนดค่าจะเสร็จสิ้น',
    },
    testConfigurationStep: {
      error__noSuccessfulTestRun: undefined,
      subtitle: undefined,
      testResults: {
        actionLabel__refresh: undefined,
        empty: {
          subtitle: undefined,
          title: undefined,
        },
        polling: undefined,
        status__failed: undefined,
        status__pending: undefined,
        status__success: undefined,
        title: undefined,
      },
      testRunDetails: {
        howToFix: {
          actionLabel__viewDocumentation: undefined,
          oauth_access_denied: {
            description: undefined,
          },
          oauth_fetch_user_error: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
          },
          oauth_token_exchange_error: {
            description: undefined,
          },
          saml_email_address_domain_mismatch: {
            description: undefined,
          },
          saml_response_relaystate_missing: {
            description: undefined,
          },
          saml_user_attribute_missing: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          sectionTitle: undefined,
        },
        parsedUserInfo: {
          email: undefined,
          firstName: undefined,
          sectionTitle: undefined,
        },
        runDetails: {
          actionLabel__copied: undefined,
          actionLabel__copy: undefined,
          errorCode: undefined,
          fullMessage: undefined,
          sectionTitle: undefined,
          status: undefined,
          timestamp: undefined,
        },
        title: undefined,
      },
      testUrl: {
        actionLabel__open: undefined,
      },
      title: undefined,
    },
  },
  createOrganization: {
    formButtonSubmit: 'สร้างองค์กร',
    invitePage: {
      formButtonReset: 'ข้าม',
    },
    title: 'สร้างองค์กร',
  },
  dates: {
    lastDay: "เมื่อวานนี้ เวลา {{ date | timeString('th-TH') }} น.",
    next6Days: "{{ date | weekday('th-TH','long') }} เวลา {{ date | timeString('th-TH') }} น.",
    nextDay: "พรุ่งนี้ เวลา {{ date | timeString('th-TH') }} น.",
    numeric: "{{ date | numeric('th-TH') }}",
    previous6Days: "{{ date | weekday('th-TH','long') }}ที่ผ่านมา เวลา {{ date | timeString('th-TH') }} น.",
    sameDay: "วันนี้ เวลา {{ date | timeString('th-TH') }} น.",
  },
  dividerText: 'หรือ',
  footerActionLink__alternativePhoneCodeProvider: 'ส่งรหัสผ่าน SMS แทน',
  footerActionLink__useAnotherMethod: 'ใช้วิธีอื่น',
  footerPageLink__help: 'ช่วยเหลือ',
  footerPageLink__privacy: 'ความเป็นส่วนตัว',
  footerPageLink__terms: 'เงื่อนไข',
  formButtonPrimary: 'ดำเนินการต่อ',
  formButtonPrimary__verify: 'ยืนยัน',
  formFieldAction__forgotPassword: 'ลืมรหัสผ่าน?',
  formFieldError__matchingPasswords: 'รหัสผ่านตรงกัน',
  formFieldError__notMatchingPasswords: 'รหัสผ่านไม่ตรงกัน',
  formFieldError__verificationLinkExpired: 'ลิงก์ยืนยันหมดอายุแล้ว โปรดขอลิงก์ใหม่',
  formFieldHintText__optional: 'ไม่บังคับ',
  formFieldHintText__slug: 'Slug คือรหัสที่มนุษย์อ่านได้และต้องไม่ซ้ำกัน มักใช้ใน URL',
  formFieldInputPlaceholder__apiKeyDescription: 'อธิบายเหตุผลที่คุณสร้างคีย์นี้',
  formFieldInputPlaceholder__apiKeyExpirationDate: 'เลือกวันที่',
  formFieldInputPlaceholder__apiKeyName: 'ใส่ชื่อคีย์ลับของคุณ',
  formFieldInputPlaceholder__backupCode: 'ใส่รหัสสำรอง',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'ลบบัญชี',
  formFieldInputPlaceholder__emailAddress: 'ใส่ที่อยู่อีเมลของคุณ',
  formFieldInputPlaceholder__emailAddress_username: 'ใส่อีเมลหรือชื่อผู้ใช้',
  formFieldInputPlaceholder__emailAddresses: 'example@email.com, example2@email.com',
  formFieldInputPlaceholder__firstName: 'ชื่อ',
  formFieldInputPlaceholder__lastName: 'นามสกุล',
  formFieldInputPlaceholder__organizationDomain: 'example.com',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'you@example.com',
  formFieldInputPlaceholder__organizationName: 'ชื่อองค์กร',
  formFieldInputPlaceholder__organizationSlug: 'my-org',
  formFieldInputPlaceholder__password: 'ใส่รหัสผ่านของคุณ',
  formFieldInputPlaceholder__phoneNumber: 'ใส่หมายเลขโทรศัพท์ของคุณ',
  formFieldInputPlaceholder__signUpPassword: undefined,
  formFieldInputPlaceholder__username: undefined,
  formFieldInput__emailAddress_format: undefined,
  formFieldLabel__apiKey: 'คีย์ API',
  formFieldLabel__apiKeyDescription: 'คำอธิบาย',
  formFieldLabel__apiKeyExpiration: 'วันหมดอายุ',
  formFieldLabel__apiKeyName: 'ชื่อคีย์ลับ',
  formFieldLabel__automaticInvitations: 'เปิดใช้การเชิญอัตโนมัติสำหรับโดเมนนี้',
  formFieldLabel__backupCode: 'รหัสสำรอง',
  formFieldLabel__confirmDeletion: 'การยืนยัน',
  formFieldLabel__confirmPassword: 'ยืนยันรหัสผ่าน',
  formFieldLabel__currentPassword: 'รหัสผ่านปัจจุบัน',
  formFieldLabel__emailAddress: 'ที่อยู่อีเมล',
  formFieldLabel__emailAddress_username: 'ที่อยู่อีเมลหรือชื่อผู้ใช้',
  formFieldLabel__emailAddresses: 'ที่อยู่อีเมล',
  formFieldLabel__firstName: 'ชื่อ',
  formFieldLabel__lastName: 'นามสกุล',
  formFieldLabel__newPassword: 'รหัสผ่านใหม่',
  formFieldLabel__organizationDomain: 'โดเมน',
  formFieldLabel__organizationDomainDeletePending: 'ลบคำเชิญและคำแนะนำที่รอดำเนินการ',
  formFieldLabel__organizationDomainEmailAddress: 'ที่อยู่อีเมลสำหรับยืนยัน',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'ใส่ที่อยู่อีเมลภายใต้โดเมนนี้เพื่อรับรหัสและยืนยันโดเมนนี้',
  formFieldLabel__organizationName: 'ชื่อ',
  formFieldLabel__organizationSlug: 'Slug',
  formFieldLabel__passkeyName: 'ชื่อของพาสคีย์',
  formFieldLabel__password: 'รหัสผ่าน',
  formFieldLabel__phoneNumber: 'หมายเลขโทรศัพท์',
  formFieldLabel__role: 'บทบาท',
  formFieldLabel__signOutOfOtherSessions: 'ออกจากระบบในอุปกรณ์อื่นทั้งหมด',
  formFieldLabel__username: 'ชื่อผู้ใช้',
  identityPreviewEditButton__emailAddress: undefined,
  identityPreviewEditButton__identifier: undefined,
  identityPreviewEditButton__phoneNumber: undefined,
  impersonationFab: {
    action__signOut: 'ออกจากระบบ',
    title: 'เข้าสู่ระบบในนาม {{identifier}}',
  },
  lastAuthenticationStrategy: 'ใช้ครั้งสุดท้าย',
  maintenanceMode: 'เรากำลังอยู่ในช่วงการบำรุงรักษาระบบ แต่ไม่ต้องกังวล ไม่น่าจะใช้เวลานานกว่าสองสามนาที',
  membershipRole__admin: 'ผู้ดูแลระบบ',
  membershipRole__basicMember: 'สมาชิก',
  membershipRole__guestMember: 'แขก',
  oauthConsent: {
    action__allow: undefined,
    action__deny: undefined,
    offlineAccessNotice: undefined,
    redirectNotice: undefined,
    redirectUriModal: {
      subtitle: undefined,
      title: undefined,
    },
    scopeList: {
      title: undefined,
    },
    subtitle: undefined,
    viewFullUrl: undefined,
    warning: undefined,
  },
  organizationList: {
    action__createOrganization: 'สร้างองค์กร',
    action__invitationAccept: 'เข้าร่วม',
    action__suggestionsAccept: 'ขอเข้าร่วม',
    createOrganization: 'สร้างองค์กร',
    invitationAcceptedLabel: 'เข้าร่วมแล้ว',
    subtitle: 'เพื่อดำเนินการต่อไปยัง {{applicationName}}',
    suggestionsAcceptedLabel: 'รออนุมัติ',
    title: 'เลือกบัญชี',
    titleWithoutPersonal: 'เลือกองค์กร',
  },
  organizationProfile: {
    apiKeysPage: {
      title: 'คีย์ API',
    },
    badge__automaticInvitation: 'การเชิญอัตโนมัติ',
    badge__automaticSuggestion: 'คำแนะนำอัตโนมัติ',
    badge__enterpriseSso: undefined,
    badge__manualInvitation: 'ไม่มีการลงทะเบียนอัตโนมัติ',
    badge__unverified: 'ยังไม่ได้ยืนยัน',
    billingPage: {
      accountCreditsSection: {
        title: undefined,
        viewHistory: undefined,
      },
      creditHistoryPage: {
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
      },
      paymentHistorySection: {
        empty: 'ไม่มีประวัติการชำระเงิน',
        notFound: 'ไม่พบความพยายามในการชำระเงิน',
        tableHeader__amount: 'จำนวนเงิน',
        tableHeader__date: 'วันที่',
        tableHeader__status: 'สถานะ',
      },
      paymentMethodsSection: {
        actionLabel__default: 'ตั้งเป็นค่าเริ่มต้น',
        actionLabel__remove: 'ลบ',
        add: 'เพิ่มวิธีชำระเงินใหม่',
        addSubtitle: 'เพิ่มวิธีการชำระเงินใหม่ในบัญชีของคุณ',
        cancelButton: 'ยกเลิก',
        formButtonPrimary__add: 'เพิ่มวิธีชำระเงิน',
        formButtonPrimary__pay: 'ชำระ {{amount}}',
        payWithTestCardButton: 'ชำระด้วยบัตรทดสอบ',
        removeMethod: {
          messageLine1: '{{identifier}} จะถูกลบออกจากบัญชีนี้',
          messageLine2:
            'คุณจะไม่สามารถใช้แหล่งชำระเงินนี้ได้อีก และการสมัครสมาชิกแบบต่ออายุอัตโนมัติที่เกี่ยวข้องจะไม่ทำงานอีกต่อไป',
          successMessage: '{{paymentMethod}} ได้ถูกลบออกจากบัญชีของคุณแล้ว',
          title: 'ลบวิธีชำระเงิน',
        },
        title: 'วิธีการชำระเงิน',
      },
      start: {
        headerTitle__payments: 'การชำระเงิน',
        headerTitle__plans: 'แผน',
        headerTitle__statements: 'ใบแจ้งหนี้',
        headerTitle__subscriptions: 'การสมัครสมาชิก',
      },
      statementsSection: {
        empty: 'ไม่มีใบแจ้งหนี้ที่จะแสดง',
        itemCaption__paidForPlan: 'ชำระสำหรับแผน {{plan}} {{period}}',
        itemCaption__payerCredit: undefined,
        itemCaption__proratedCredit: 'เครดิตตามสัดส่วนสำหรับการใช้งานบางส่วนของการสมัครสมาชิกก่อนหน้า',
        itemCaption__subscribedAndPaidForPlan: 'สมัครสมาชิกและชำระสำหรับแผน {{plan}} {{period}}',
        notFound: 'ไม่พบใบแจ้งหนี้',
        tableHeader__amount: 'จำนวนเงิน',
        tableHeader__date: 'วันที่',
        title: 'ใบแจ้งหนี้',
        totalPaid: 'รวมที่ชำระ',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: undefined,
        actionLabel__newSubscription: 'สมัครสมาชิกแผน',
        actionLabel__switchPlan: 'เปลี่ยนแผน',
        includedSeatsUsage: undefined,
        overview: undefined,
        paidSeatsUsage: undefined,
        seatLimit: undefined,
        seatLimitAndIncludedSeats: undefined,
        tableHeader__edit: 'แก้ไข',
        tableHeader__plan: 'แผน',
        tableHeader__startDate: 'วันที่เริ่มต้น',
        title: 'การสมัครสมาชิก',
      },
      subscriptionsSection: {
        actionLabel__default: 'จัดการ',
      },
      switchPlansSection: {
        title: 'เปลี่ยนแผน',
      },
      title: 'การเรียกเก็บเงิน',
    },
    createDomainPage: {
      subtitle:
        'เพิ่มโดเมนเพื่อยืนยัน ผู้ใช้ที่มีที่อยู่อีเมลในโดเมนนี้สามารถเข้าร่วมองค์กรได้โดยอัตโนมัติหรือขอเข้าร่วม',
      title: 'เพิ่มโดเมน',
    },
    invitePage: {
      detailsTitle__inviteFailed:
        'ไม่สามารถส่งคำเชิญได้ มีคำเชิญที่รอดำเนินการอยู่แล้วสำหรับที่อยู่อีเมลต่อไปนี้: {{email_addresses}}',
      formButtonPrimary__continue: 'ส่งคำเชิญ',
      formButtonPrimary__purchaseSeats: undefined,
      selectDropdown__role: 'เลือกบทบาท',
      subtitle: 'ใส่หรือวางที่อยู่อีเมลตั้งแต่หนึ่งรายการขึ้นไป คั่นด้วยช่องว่างหรือเครื่องหมายจุลภาค',
      successMessage: 'ส่งคำเชิญสำเร็จ',
      title: 'เชิญสมาชิกใหม่',
    },
    membersPage: {
      action__invite: 'เชิญ',
      action__search: 'ค้นหา',
      activeMembersTab: {
        menuAction__remove: 'ลบสมาชิก',
        tableHeader__actions: 'การดำเนินการ',
        tableHeader__joined: 'เข้าร่วม',
        tableHeader__role: 'บทบาท',
        tableHeader__user: 'ผู้ใช้',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle: 'เรากำลังอัปเดตบทบาทที่มีอยู่ เมื่อเสร็จแล้ว คุณจะสามารถอัปเดตบทบาทได้อีกครั้ง',
          title: 'บทบาทถูกล็อคชั่วคราว',
        },
      },
      detailsTitle__emptyRow: 'ไม่มีสมาชิกที่จะแสดง',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'เชิญผู้ใช้โดยการเชื่อมต่อโดเมนอีเมลกับองค์กรของคุณ ผู้ที่สมัครด้วยโดเมนอีเมลที่ตรงกันจะสามารถเข้าร่วมองค์กรได้ทุกเวลา',
          headerTitle: 'การเชิญอัตโนมัติ',
          primaryButton: 'จัดการโดเมนที่ยืนยันแล้ว',
        },
        table__emptyRow: 'ไม่มีคำเชิญที่จะแสดง',
      },
      invitedMembersTab: {
        menuAction__revoke: 'เพิกถอนคำเชิญ',
        tableHeader__invited: 'เชิญแล้ว',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle: 'ผู้ใช้ที่สมัครด้วยโดเมนอีเมลที่ตรงกันจะสามารถเห็นคำแนะนำให้ขอเข้าร่วมองค์กรของคุณ',
          headerTitle: 'คำแนะนำอัตโนมัติ',
          primaryButton: 'จัดการโดเมนที่ยืนยันแล้ว',
        },
        menuAction__approve: 'อนุมัติ',
        menuAction__reject: 'ปฏิเสธ',
        tableHeader__requested: 'ขอสิทธิ์เข้าถึง',
        table__emptyRow: 'ไม่มีคำขอที่จะแสดง',
      },
      start: {
        headerTitle__invitations: 'คำเชิญ',
        headerTitle__members: 'สมาชิก',
        headerTitle__requests: 'คำขอ',
      },
    },
    navbar: {
      apiKeys: 'คีย์ API',
      billing: 'การเรียกเก็บเงิน',
      description: 'จัดการองค์กรของคุณ',
      general: 'ทั่วไป',
      members: 'สมาชิก',
      security: undefined,
      title: 'องค์กร',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: 'คุณไม่มีสิทธิ์ในการจัดการการเรียกเก็บเงินสำหรับองค์กรนี้',
        planMembershipLimitExceeded: undefined,
      },
      title: 'แผน',
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'พิมพ์ "{{organizationName}}" ด้านล่างเพื่อดำเนินการต่อ',
          messageLine1: 'คุณแน่ใจหรือไม่ว่าต้องการลบองค์กรนี้?',
          messageLine2: 'การดำเนินการนี้จะถาวรและไม่สามารถยกเลิกได้',
          successMessage: 'คุณได้ลบองค์กรแล้ว',
          title: 'ลบองค์กร',
        },
        leaveOrganization: {
          actionDescription: 'พิมพ์ "{{organizationName}}" ด้านล่างเพื่อดำเนินการต่อ',
          messageLine1: 'คุณแน่ใจหรือไม่ว่าต้องการออกจากองค์กรนี้? คุณจะสูญเสียการเข้าถึงองค์กรนี้และแอปพลิเคชันต่าง ๆ',
          messageLine2: 'การดำเนินการนี้จะถาวรและไม่สามารถยกเลิกได้',
          successMessage: 'คุณได้ออกจากองค์กรแล้ว',
          title: 'ออกจากองค์กร',
        },
        title: 'อันตราย',
      },
      domainSection: {
        menuAction__manage: 'จัดการ',
        menuAction__remove: 'ลบ',
        menuAction__verify: 'ยืนยัน',
        primaryButton: 'เพิ่มโดเมน',
        subtitle: 'อนุญาตให้ผู้ใช้เข้าร่วมองค์กรโดยอัตโนมัติหรือขอเข้าร่วมโดยอิงจากโดเมนอีเมลที่ยืนยันแล้ว',
        title: 'โดเมนที่ยืนยันแล้ว',
      },
      successMessage: 'องค์กรได้รับการอัปเดตแล้ว',
      title: 'อัปเดตโปรไฟล์',
    },
    removeDomainPage: {
      messageLine1: 'โดเมนอีเมล {{domain}} จะถูกลบ',
      messageLine2: 'ผู้ใช้จะไม่สามารถเข้าร่วมองค์กรโดยอัตโนมัติได้หลังจากนี้',
      successMessage: '{{domain}} ได้ถูกลบแล้ว',
      title: 'ลบโดเมน',
    },
    securityPage: {
      removeDialog: {
        confirmButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
      ssoSection: {
        badge__active: undefined,
        badge__inProgress: undefined,
        badge__inactive: undefined,
        badge__unconfigured: undefined,
        descriptionLine1: undefined,
        domainLabel: undefined,
        menuAction__activate: undefined,
        menuAction__deactivate: undefined,
        menuAction__edit: undefined,
        menuAction__remove: undefined,
        primaryButton__continueConfiguration: undefined,
        primaryButton__startConfiguration: undefined,
        title: undefined,
        tooltip: undefined,
        tooltipLabel: undefined,
        tooltip__noRole: undefined,
      },
      title: undefined,
    },
    start: {
      headerTitle__general: 'ทั่วไป',
      headerTitle__members: 'สมาชิก',
      membershipSeatUsageLabel: undefined,
      profileSection: {
        primaryButton: 'อัปเดตโปรไฟล์',
        title: 'โปรไฟล์องค์กร',
        uploadAction__title: 'โลโก้',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'การลบโดเมนนี้จะส่งผลต่อผู้ใช้ที่ได้รับเชิญ',
        removeDomainActionLabel__remove: 'ลบโดเมน',
        removeDomainSubtitle: 'ลบโดเมนนี้จากโดเมนที่ยืนยันของคุณ',
        removeDomainTitle: 'ลบโดเมน',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'ผู้ใช้จะได้รับเชิญให้เข้าร่วมองค์กรโดยอัตโนมัติเมื่อพวกเขาสมัครและสามารถเข้าร่วมได้ทุกเวลา',
        automaticInvitationOption__label: 'การเชิญอัตโนมัติ',
        automaticSuggestionOption__description:
          'ผู้ใช้จะได้รับคำแนะนำให้ขอเข้าร่วม แต่ต้องได้รับการอนุมัติจากผู้ดูแลก่อนที่จะสามารถเข้าร่วมองค์กรได้',
        automaticSuggestionOption__label: 'คำแนะนำอัตโนมัติ',
        calloutInfoLabel: 'การเปลี่ยนโหมดการลงทะเบียนจะส่งผลต่อผู้ใช้ใหม่เท่านั้น',
        calloutInvitationCountLabel: 'คำเชิญที่รอดำเนินการส่งให้ผู้ใช้: {{count}}',
        calloutSuggestionCountLabel: 'คำแนะนำที่รอดำเนินการส่งให้ผู้ใช้: {{count}}',
        manualInvitationOption__description: 'ผู้ใช้สามารถได้รับเชิญเข้าองค์กรด้วยตนเองเท่านั้น',
        manualInvitationOption__label: 'ไม่มีการลงทะเบียนอัตโนมัติ',
        subtitle: 'เลือกวิธีที่ผู้ใช้จากโดเมนนี้สามารถเข้าร่วมองค์กร',
      },
      start: {
        headerTitle__danger: 'อันตราย',
        headerTitle__enrollment: 'ตัวเลือกการลงทะเบียน',
      },
      subtitle: 'โดเมน {{domain}} ได้รับการยืนยันแล้ว ดำเนินการต่อโดยเลือกโหมดการลงทะเบียน',
      title: 'อัปเดต {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'ใส่รหัสยืนยันที่ส่งไปยังที่อยู่อีเมลของคุณ',
      formTitle: 'รหัสยืนยัน',
      resendButton: 'ไม่ได้รับรหัส? ส่งใหม่',
      subtitle: 'โดเมน {{domainName}} ต้องได้รับการยืนยันผ่านอีเมล',
      subtitleVerificationCodeScreen: 'รหัสยืนยันถูกส่งไปยัง {{emailAddress}} ใส่รหัสเพื่อดำเนินการต่อ',
      title: 'ยืนยันโดเมน',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: undefined,
    action__createOrganization: 'สร้างองค์กร',
    action__invitationAccept: 'เข้าร่วม',
    action__manageOrganization: 'จัดการ',
    action__openOrganizationSwitcher: undefined,
    action__suggestionsAccept: 'ขอเข้าร่วม',
    notSelected: 'ไม่ได้เลือกองค์กร',
    personalWorkspace: 'บัญชีส่วนตัว',
    suggestionsAcceptedLabel: 'รออนุมัติ',
  },
  paginationButton__next: 'ถัดไป',
  paginationButton__previous: 'ก่อนหน้า',
  paginationRowText__displaying: 'แสดง',
  paginationRowText__of: 'จาก',
  reverification: {
    alternativeMethods: {
      actionLink: 'รับความช่วยเหลือ',
      actionText: 'ไม่สามารถเข้าถึงวิธีเหล่านี้?',
      blockButton__backupCode: 'ใช้รหัสสำรอง',
      blockButton__emailCode: 'ส่งรหัสทางอีเมลไปยัง {{identifier}}',
      blockButton__passkey: 'ใช้พาสคีย์ของคุณ',
      blockButton__password: 'ดำเนินการต่อด้วยรหัสผ่านของคุณ',
      blockButton__phoneCode: 'ส่งรหัส SMS ไปยัง {{identifier}}',
      blockButton__totp: 'ใช้แอป Authenticator ของคุณ',
      getHelp: {
        blockButton__emailSupport: 'อีเมลฝ่ายสนับสนุน',
        content:
          'หากคุณมีปัญหาในการยืนยันบัญชี ส่งอีเมลมาหาเราแล้วเราจะร่วมกันทำงานเพื่อคืนสิทธิ์การเข้าถึงโดยเร็วที่สุด',
        title: 'รับความช่วยเหลือ',
      },
      subtitle: 'พบปัญหา? คุณสามารถใช้วิธีใดก็ได้จากวิธีเหล่านี้เพื่อยืนยัน',
      title: 'ใช้วิธีอื่น',
    },
    backupCodeMfa: {
      subtitle: 'ใส่รหัสสำรองที่คุณได้รับเมื่อตั้งค่าการยืนยันตัวตนสองขั้นตอน',
      title: 'ใส่รหัสสำรอง',
    },
    emailCode: {
      formTitle: 'รหัสยืนยัน',
      resendButton: 'ไม่ได้รับรหัส? ส่งใหม่',
      subtitle: 'ใส่รหัสที่ส่งไปยังอีเมลของคุณเพื่อดำเนินการต่อ',
      title: 'ต้องการการยืนยัน',
    },
    noAvailableMethods: {
      message: 'ไม่สามารถดำเนินการยืนยันได้ ไม่มีปัจจัยการยืนยันตัวตนที่เหมาะสมได้รับการกำหนดค่า',
      subtitle: 'เกิดข้อผิดพลาด',
      title: 'ไม่สามารถยืนยันบัญชีของคุณ',
    },
    passkey: {
      blockButton__passkey: 'ใช้พาสคีย์ของคุณ',
      subtitle: 'การใช้พาสคีย์ของคุณยืนยันตัวตนของคุณ อุปกรณ์ของคุณอาจขอลายนิ้วมือ ใบหน้า หรือการล็อคหน้าจอ',
      title: 'ใช้พาสคีย์ของคุณ',
    },
    password: {
      actionLink: 'ใช้วิธีอื่น',
      subtitle: 'ใส่รหัสผ่านปัจจุบันของคุณเพื่อดำเนินการต่อ',
      title: 'ต้องการการยืนยัน',
    },
    phoneCode: {
      formTitle: 'รหัสยืนยัน',
      resendButton: 'ไม่ได้รับรหัส? ส่งใหม่',
      subtitle: 'ใส่รหัสที่ส่งไปยังโทรศัพท์ของคุณเพื่อดำเนินการต่อ',
      title: 'ต้องการการยืนยัน',
    },
    phoneCodeMfa: {
      formTitle: 'รหัสยืนยัน',
      resendButton: 'ไม่ได้รับรหัส? ส่งใหม่',
      subtitle: 'ใส่รหัสที่ส่งไปยังโทรศัพท์ของคุณเพื่อดำเนินการต่อ',
      title: 'ต้องการการยืนยัน',
    },
    totpMfa: {
      formTitle: 'รหัสยืนยัน',
      subtitle: 'ใส่รหัสที่สร้างโดยแอป Authenticator ของคุณเพื่อดำเนินการต่อ',
      title: 'ต้องการการยืนยัน',
    },
  },
  searchInput: {
    action__clear: undefined,
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'เพิ่มบัญชี',
      action__signOutAll: 'ออกจากระบบทุกบัญชี',
      subtitle: 'เลือกบัญชีที่คุณต้องการดำเนินการต่อ',
      title: 'เลือกบัญชี',
    },
    alternativeMethods: {
      actionLink: 'รับความช่วยเหลือ',
      actionText: 'ไม่มีวิธีเหล่านี้?',
      blockButton__backupCode: 'ใช้รหัสสำรอง',
      blockButton__emailCode: 'ส่งรหัสทางอีเมลไปยัง {{identifier}}',
      blockButton__emailLink: 'ส่งลิงก์ทางอีเมลไปยัง {{identifier}}',
      blockButton__passkey: 'เข้าสู่ระบบด้วยพาสคีย์ของคุณ',
      blockButton__password: 'เข้าสู่ระบบด้วยรหัสผ่านของคุณ',
      blockButton__phoneCode: 'ส่ง SMS รหัสไปยัง {{identifier}}',
      blockButton__totp: 'ใช้แอป Authenticator ของคุณ',
      getHelp: {
        blockButton__emailSupport: 'อีเมลฝ่ายสนับสนุน',
        content:
          'หากคุณมีปัญหาในการเข้าสู่ระบบบัญชี ส่งอีเมลมาหาเราแล้วเราจะร่วมกันทำงานเพื่อคืนสิทธิ์การเข้าถึงโดยเร็วที่สุด',
        title: 'รับความช่วยเหลือ',
      },
      subtitle: 'พบปัญหา? คุณสามารถใช้วิธีใดก็ได้จากวิธีเหล่านี้เพื่อเข้าสู่ระบบ',
      title: 'ใช้วิธีอื่น',
    },
    alternativePhoneCodeProvider: {
      formTitle: 'รหัสยืนยัน',
      resendButton: 'ไม่ได้รับรหัส? ส่งใหม่',
      subtitle: 'เพื่อดำเนินการต่อไปยัง {{applicationName}}',
      title: 'ตรวจสอบ {{provider}} ของคุณ',
    },
    backupCodeMfa: {
      subtitle: 'รหัสสำรองของคุณคือรหัสที่คุณได้รับเมื่อตั้งค่าการยืนยันตัวตนสองขั้นตอน',
      title: 'ใส่รหัสสำรอง',
    },
    emailCode: {
      formTitle: 'รหัสยืนยัน',
      resendButton: 'ไม่ได้รับรหัส? ส่งใหม่',
      subtitle: 'เพื่อดำเนินการต่อไปยัง {{applicationName}}',
      title: 'ตรวจสอบอีเมลของคุณ',
    },
    emailCodeMfa: {
      formTitle: 'ตรวจสอบอีเมลของคุณ',
      resendButton: 'ไม่ได้รับรหัส? ส่งใหม่',
      subtitle: 'เพื่อดำเนินการต่อไปยัง {{applicationName}}',
      title: 'ตรวจสอบอีเมลของคุณ',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'เพื่อดำเนินการต่อ เปิดลิงก์ยืนยันในอุปกรณ์และเบราว์เซอร์ที่คุณเริ่มต้นการเข้าสู่ระบบ',
        title: 'ลิงก์ยืนยันไม่ถูกต้องสำหรับอุปกรณ์นี้',
      },
      expired: {
        subtitle: 'กลับไปยังแท็บเดิมเพื่อดำเนินการต่อ',
        title: 'ลิงก์ยืนยันนี้หมดอายุแล้ว',
      },
      failed: {
        subtitle: 'กลับไปยังแท็บเดิมเพื่อดำเนินการต่อ',
        title: 'ลิงก์ยืนยันนี้ไม่ถูกต้อง',
      },
      formSubtitle: 'ใช้ลิงก์ยืนยันที่ส่งไปยังอีเมลของคุณ',
      formTitle: 'ลิงก์ยืนยัน',
      loading: {
        subtitle: 'คุณจะถูกเปลี่ยนเส้นทางในไม่ช้า',
        title: 'กำลังเข้าสู่ระบบ...',
      },
      resendButton: 'ไม่ได้รับลิงก์? ส่งใหม่',
      subtitle: 'เพื่อดำเนินการต่อไปยัง {{applicationName}}',
      title: 'ตรวจสอบอีเมลของคุณ',
      unusedTab: {
        title: 'คุณสามารถปิดแท็บนี้ได้',
      },
      verified: {
        subtitle: 'คุณจะถูกเปลี่ยนเส้นทางในไม่ช้า',
        title: 'เข้าสู่ระบบสำเร็จ',
      },
      verifiedSwitchTab: {
        subtitle: 'กลับไปยังแท็บเดิมเพื่อดำเนินการต่อ',
        subtitleNewTab: 'กลับไปยังแท็บที่เปิดใหม่เพื่อดำเนินการต่อ',
        titleNewTab: 'เข้าสู่ระบบในแท็บอื่น',
      },
    },
    emailLinkMfa: {
      formSubtitle: 'ใช้ลิงก์ยืนยันที่ส่งไปยังอีเมลของคุณ',
      resendButton: 'ไม่ได้รับลิงก์? ส่งอีกครั้ง',
      subtitle: 'เพื่อดำเนินการต่อที่ {{applicationName}}',
      title: 'ตรวจสอบอีเมลของคุณ',
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    forgotPassword: {
      formTitle: 'รหัสรีเซ็ตรหัสผ่าน',
      resendButton: 'ไม่ได้รับรหัส? ส่งใหม่',
      subtitle: 'เพื่อรีเซ็ตรหัสผ่านของคุณ',
      subtitle_email: 'ก่อนอื่น ใส่รหัสที่ส่งไปยังที่อยู่อีเมลของคุณ',
      subtitle_phone: 'ก่อนอื่น ใส่รหัสที่ส่งไปยังโทรศัพท์ของคุณ',
      title: 'รีเซ็ตรหัสผ่าน',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'รีเซ็ตรหัสผ่านของคุณ',
      label__alternativeMethods: 'หรือ เข้าสู่ระบบด้วยวิธีอื่น',
      title: 'ลืมรหัสผ่าน?',
    },
    newDeviceVerificationNotice:
      'คุณกำลังเข้าสู่ระบบจากอุปกรณ์ใหม่ เรากำลังขอการยืนยันเพื่อรักษาความปลอดภัยของบัญชีของคุณ',
    noAvailableMethods: {
      message: 'ไม่สามารถดำเนินการเข้าสู่ระบบได้ ไม่มีปัจจัยการยืนยันตัวตนที่สามารถใช้ได้',
      subtitle: 'เกิดข้อผิดพลาด',
      title: 'ไม่สามารถเข้าสู่ระบบ',
    },
    passkey: {
      subtitle: 'การใช้พาสคีย์ของคุณยืนยันว่าเป็นคุณ อุปกรณ์ของคุณอาจขอลายนิ้วมือ ใบหน้า หรือการล็อคหน้าจอ',
      title: 'ใช้พาสคีย์ของคุณ',
    },
    password: {
      actionLink: 'ใช้วิธีอื่น',
      subtitle: 'ใส่รหัสผ่านที่เชื่อมโยงกับบัญชีของคุณ',
      title: 'ใส่รหัสผ่านของคุณ',
    },
    passwordCompromised: {
      title: undefined,
    },
    passwordPwned: {
      title: 'รหัสผ่านถูกโจรกรรม',
    },
    passwordUntrusted: {
      title: undefined,
    },
    phoneCode: {
      formTitle: 'รหัสยืนยัน',
      resendButton: 'ไม่ได้รับรหัส? ส่งใหม่',
      subtitle: 'เพื่อดำเนินการต่อไปยัง {{applicationName}}',
      title: 'ตรวจสอบโทรศัพท์ของคุณ',
    },
    phoneCodeMfa: {
      formTitle: 'รหัสยืนยัน',
      resendButton: 'ไม่ได้รับรหัส? ส่งใหม่',
      subtitle: 'เพื่อดำเนินการต่อ โปรดใส่รหัสยืนยันที่ส่งไปยังโทรศัพท์ของคุณ',
      title: 'ตรวจสอบโทรศัพท์ของคุณ',
    },
    protectCheck: {
      loading: undefined,
      retryButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    resetPassword: {
      formButtonPrimary: 'รีเซ็ตรหัสผ่าน',
      requiredMessage: 'เพื่อความปลอดภัย จำเป็นต้องรีเซ็ตรหัสผ่านของคุณ',
      successMessage: 'รหัสผ่านของคุณได้รับการเปลี่ยนแปลงสำเร็จ กำลังเข้าสู่ระบบ โปรดรอสักครู่',
      title: 'ตั้งรหัสผ่านใหม่',
    },
    resetPasswordMfa: {
      detailsLabel: 'เราต้องยืนยันตัวตนของคุณก่อนรีเซ็ตรหัสผ่าน',
    },
    start: {
      actionLink: 'สมัครสมาชิก',
      actionLink__join_waitlist: 'เข้าร่วม Waitlist',
      actionLink__use_email: 'ใช้อีเมล',
      actionLink__use_email_username: 'ใช้อีเมลหรือชื่อผู้ใช้',
      actionLink__use_passkey: 'ใช้พาสคีย์แทน',
      actionLink__use_phone: 'ใช้โทรศัพท์',
      actionLink__use_username: 'ใช้ชื่อผู้ใช้',
      actionText: 'ไม่มีบัญชี?',
      actionText__join_waitlist: 'ต้องการการเข้าถึงก่อนใคร?',
      alternativePhoneCodeProvider: {
        actionLink: 'ใช้วิธีอื่น',
        label: 'หมายเลขโทรศัพท์ {{provider}}',
        subtitle: 'ใส่หมายเลขโทรศัพท์ของคุณเพื่อรับรหัสยืนยันทาง {{provider}}',
        title: 'เข้าสู่ระบบ {{applicationName}} ด้วย {{provider}}',
      },
      subtitle: 'ยินดีต้อนรับกลับ! โปรดเข้าสู่ระบบเพื่อดำเนินการต่อ',
      subtitleCombined: undefined,
      title: 'เข้าสู่ระบบ {{applicationName}}',
      titleCombined: 'ดำเนินการต่อไปยัง {{applicationName}}',
    },
    totpMfa: {
      formTitle: 'รหัสยืนยัน',
      subtitle: 'เพื่อดำเนินการต่อ โปรดใส่รหัสยืนยันที่สร้างโดยแอป Authenticator ของคุณ',
      title: 'การยืนยันตัวตนสองขั้นตอน',
    },
    web3Solana: {
      subtitle: 'เลือกกระเป๋าเงินด้านล่างเพื่อเข้าสู่ระบบ',
      title: 'เข้าสู่ระบบด้วย Solana',
    },
  },
  signInEnterPasswordTitle: 'ใส่รหัสผ่านของคุณ',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: 'ไม่ได้รับรหัส? ส่งใหม่',
      subtitle: 'ใส่รหัสยืนยันที่ส่งไปยัง {{provider}} ของคุณ',
      title: 'ยืนยัน {{provider}} ของคุณ',
    },
    continue: {
      actionLink: 'เข้าสู่ระบบ',
      actionText: 'มีบัญชีอยู่แล้ว?',
      subtitle: 'โปรดกรอกข้อมูลที่เหลือเพื่อดำเนินการต่อ',
      title: 'กรอกข้อมูลที่ขาดหายไป',
    },
    emailCode: {
      formSubtitle: 'ใส่รหัสยืนยันที่ส่งไปยังที่อยู่อีเมลของคุณ',
      formTitle: 'รหัสยืนยัน',
      resendButton: 'ไม่ได้รับรหัส? ส่งใหม่',
      subtitle: 'ใส่รหัสยืนยันที่ส่งไปยังอีเมลของคุณ',
      title: 'ยืนยันอีเมลของคุณ',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'เพื่อดำเนินการต่อ เปิดลิงก์ยืนยันในอุปกรณ์และเบราว์เซอร์ที่คุณเริ่มต้นการสมัครสมาชิก',
        title: 'ลิงก์ยืนยันไม่ถูกต้องสำหรับอุปกรณ์นี้',
      },
      formSubtitle: 'ใช้ลิงก์ยืนยันที่ส่งไปยังที่อยู่อีเมลของคุณ',
      formTitle: 'ลิงก์ยืนยัน',
      loading: {
        title: 'กำลังสมัครสมาชิก...',
      },
      resendButton: 'ไม่ได้รับลิงก์? ส่งใหม่',
      subtitle: 'เพื่อดำเนินการต่อไปยัง {{applicationName}}',
      title: 'ยืนยันอีเมลของคุณ',
      verified: {
        title: 'สมัครสมาชิกสำเร็จ',
      },
      verifiedSwitchTab: {
        subtitle: 'กลับไปยังแท็บที่เปิดใหม่เพื่อดำเนินการต่อ',
        subtitleNewTab: 'กลับไปยังแท็บก่อนหน้าเพื่อดำเนินการต่อ',
        title: 'ยืนยันอีเมลสำเร็จ',
      },
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'ฉันยอมรับ {{ privacyPolicyLink || link("นโยบายความเป็นส่วนตัว") }}',
        label__onlyTermsOfService: 'ฉันยอมรับ {{ termsOfServiceLink || link("เงื่อนไขการให้บริการ") }}',
        label__termsOfServiceAndPrivacyPolicy:
          'ฉันยอมรับ {{ termsOfServiceLink || link("เงื่อนไขการให้บริการ") }} และ {{ privacyPolicyLink || link("นโยบายความเป็นส่วนตัว") }}',
      },
      continue: {
        subtitle: 'โปรดอ่านและยอมรับเงื่อนไขเพื่อดำเนินการต่อ',
        title: 'ความยินยอมทางกฎหมาย',
      },
    },
    phoneCode: {
      formSubtitle: 'ใส่รหัสยืนยันที่ส่งไปยังหมายเลขโทรศัพท์ของคุณ',
      formTitle: 'รหัสยืนยัน',
      resendButton: 'ไม่ได้รับรหัส? ส่งใหม่',
      subtitle: 'ใส่รหัสยืนยันที่ส่งไปยังโทรศัพท์ของคุณ',
      title: 'ยืนยันโทรศัพท์ของคุณ',
    },
    protectCheck: {
      loading: undefined,
      retryButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    restrictedAccess: {
      actionLink: 'เข้าสู่ระบบ',
      actionText: 'มีบัญชีอยู่แล้ว?',
      blockButton__emailSupport: 'อีเมลฝ่ายสนับสนุน',
      blockButton__joinWaitlist: 'เข้าร่วม Waitlist',
      subtitle: 'การสมัครสมาชิกถูกปิดใช้งานในขณะนี้ หากคุณเชื่อว่าคุณควรมีสิทธิ์เข้าถึง โปรดติดต่อฝ่ายสนับสนุน',
      subtitleWaitlist: 'การสมัครสมาชิกถูกปิดใช้งานในขณะนี้ เพื่อเป็นคนแรกที่รู้เมื่อเราเปิดตัว เข้าร่วม Waitlist',
      title: 'การเข้าถึงถูกจำกัด',
    },
    start: {
      actionLink: 'เข้าสู่ระบบ',
      actionLink__use_email: 'ใช้อีเมลแทน',
      actionLink__use_phone: 'ใช้โทรศัพท์แทน',
      actionText: 'มีบัญชีอยู่แล้ว?',
      alternativePhoneCodeProvider: {
        actionLink: 'ใช้วิธีอื่น',
        label: 'หมายเลขโทรศัพท์ {{provider}}',
        subtitle: 'ใส่หมายเลขโทรศัพท์ของคุณเพื่อรับรหัสยืนยันทาง {{provider}}',
        title: 'สมัครสมาชิก {{applicationName}} ด้วย {{provider}}',
      },
      subtitle: 'ยินดีต้อนรับ! โปรดกรอกรายละเอียดเพื่อเริ่มต้น',
      subtitleCombined: 'ยินดีต้อนรับ! โปรดกรอกรายละเอียดเพื่อเริ่มต้น',
      title: 'สร้างบัญชีของคุณ',
      titleCombined: 'สร้างบัญชีของคุณ',
    },
    web3Solana: {
      subtitle: 'เลือกกระเป๋าเงินด้านล่างเพื่อสมัครใช้งาน',
      title: 'สมัครใช้งานด้วย Solana',
    },
  },
  socialButtonsBlockButton: 'ดำเนินการต่อด้วย {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        'องค์กรสำหรับชื่อบริษัทที่ตรวจพบ ({{organizationName}}) และ {{organizationDomain}} มีอยู่แล้ว เข้าร่วมโดยการเชิญ',
    },
    chooseOrganization: {
      action__createOrganization: 'สร้างองค์กรใหม่',
      action__invitationAccept: 'เข้าร่วม',
      action__suggestionsAccept: 'ขอเข้าร่วม',
      subtitle: 'เข้าร่วมองค์กรที่มีอยู่หรือสร้างใหม่',
      subtitle__createOrganizationDisabled: 'เข้าร่วมองค์กรที่มีอยู่',
      suggestionsAcceptedLabel: 'รออนุมัติ',
      title: 'เลือกองค์กร',
    },
    createOrganization: {
      formButtonReset: 'ยกเลิก',
      formButtonSubmit: 'ดำเนินการต่อ',
      formFieldInputPlaceholder__name: 'องค์กรของฉัน',
      formFieldInputPlaceholder__slug: 'my-organization',
      formFieldLabel__name: 'ชื่อ',
      formFieldLabel__slug: 'Slug',
      subtitle: 'ใส่รายละเอียดองค์กรของคุณเพื่อดำเนินการต่อ',
      title: 'ตั้งค่าองค์กรของคุณ',
    },
    organizationCreationDisabled: {
      subtitle: 'ติดต่อผู้ดูแลระบบขององค์กรของคุณเพื่อขอคำเชิญ',
      title: 'คุณต้องเป็นสมาชิกขององค์กร',
    },
    signOut: {
      actionLink: 'ออกจากระบบ',
      actionText: 'เข้าสู่ระบบในนาม {{identifier}}',
    },
  },
  taskResetPassword: {
    formButtonPrimary: undefined,
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    subtitle: undefined,
    title: undefined,
  },
  taskSetupMfa: {
    badge: undefined,
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: undefined,
        infoText: undefined,
      },
      addPhoneNumber: undefined,
      cancel: undefined,
      subtitle: undefined,
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyPhone: {
        formButtonPrimary: undefined,
        formTitle: undefined,
        resendButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
    start: {
      methodSelection: {
        phoneCode: undefined,
        totp: undefined,
      },
      subtitle: undefined,
      title: undefined,
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: undefined,
        buttonUnableToScan__nonPrimary: undefined,
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        infoText__ableToScan: undefined,
        infoText__unableToScan: undefined,
        inputLabel__unableToScan1: undefined,
      },
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyTotp: {
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        formTitle: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
  },
  unstable__errors: {
    action_blocked: undefined,
    already_a_member_in_organization: '{{email}} เป็นสมาชิกขององค์กรอยู่แล้ว',
    api_key_name_already_exists: undefined,
    api_key_usage_exceeded: undefined,
    avatar_file_size_exceeded: 'ขนาดไฟล์เกินขีดจำกัดสูงสุด 10MB กรุณาเลือกไฟล์ที่เล็กกว่า',
    avatar_file_type_invalid: 'ประเภทไฟล์ไม่รองรับ กรุณาอัปโหลดรูปภาพ JPG, PNG, GIF หรือ WEBP',
    captcha_invalid: undefined,
    captcha_unavailable:
      'การสมัครสมาชิกไม่สำเร็จเนื่องจากการตรวจสอบความปลอดถัยล้มเหลว โปรดรีเฟรชหน้าเพื่อลองใหม่หรือติดต่อฝ่ายสนับสนุนเพื่อขอความช่วยเหลือเพิ่มเติม',
    form_code_incorrect: undefined,
    form_email_address_blocked: 'บริการอีเมลชั่วคราวไม่ได้รับการสนับสนุน กรุณาใช้อีเมลปกติของคุณเพื่อสร้างบัญชี',
    form_identifier_exists__email_address: undefined,
    form_identifier_exists__phone_number: undefined,
    form_identifier_exists__username: undefined,
    form_identifier_not_found: undefined,
    form_new_password_matches_current: undefined,
    form_param_format_invalid: undefined,
    form_param_format_invalid__email_address: undefined,
    form_param_format_invalid__phone_number: undefined,
    form_param_max_length_exceeded__first_name: undefined,
    form_param_max_length_exceeded__last_name: undefined,
    form_param_max_length_exceeded__name: undefined,
    form_param_nil: undefined,
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: undefined,
    form_password_compromised__sign_in: undefined,
    form_password_incorrect: undefined,
    form_password_length_too_short: 'รหัสผ่านของคุณสั้นเกินไป ต้องมีความยาวอย่างน้อย 8 ตัวอักษร',
    form_password_not_strong_enough: 'รหัสผ่านของคุณไม่แข็งแกร่งพอ',
    form_password_or_identifier_incorrect: 'รหัสผ่านหรือที่อยู่อีเมลไม่ถูกต้อง ลองอีกครั้งหรือใช้วิธีอื่น',
    form_password_pwned:
      'รหัสผ่านนี้ถูกพบว่าเป็นส่วนหนึ่งของรหัสผ่านที่เคยถูกโจรกรรมข้อมูลและไม่สามารถใช้ได้ โปรดลองใช้รหัสผ่านอื่นแทน',
    form_password_pwned__sign_in:
      'รหัสผ่านนี้ถูกพบว่าเป็นส่วนหนึ่งของรหัสผ่านที่เคยถูกโจรกรรมข้อมูลและไม่สามารถใช้ได้ โปรดรีเซ็ตรหัสผ่านของคุณ',
    form_password_size_in_bytes_exceeded: undefined,
    form_password_untrusted__sign_in: undefined,
    form_password_validation_failed: undefined,
    form_username_invalid_character: undefined,
    form_username_invalid_length: 'ชื่อผู้ใช้ของคุณต้องมีความยาวระหว่าง {{min_length}} ถึง {{max_length}} ตัวอักษร',
    form_username_needs_non_number_char: 'ชื่อผู้ใช้ของคุณต้องมีอักขระที่ไม่ใช่ตัวเลขอย่างน้อยหนึ่งตัว',
    identification_deletion_failed: undefined,
    insufficient_seats_change_plan: undefined,
    insufficient_seats_contact_support: undefined,
    not_allowed_access: undefined,
    oauth_access_denied: undefined,
    organization_domain_blocked: undefined,
    organization_domain_common: undefined,
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded: undefined,
    organization_minimum_permissions_needed: undefined,
    organization_not_found_or_unauthorized: 'คุณไม่เป็นสมาชิกขององค์กรนี้อีกต่อไป โปรดเลือกหรือสร้างองค์กรอื่น',
    organization_not_found_or_unauthorized_with_create_organization_disabled:
      'คุณไม่เป็นสมาชิกขององค์กรนี้อีกต่อไป โปรดเลือกองค์กรอื่น',
    passkey_already_exists: 'พาสคีย์ได้ถูกลงทะเบียนกับอุปกรณ์นี้แล้ว',
    passkey_not_supported: 'พาสคีย์ไม่ได้รับการสนับสนุนในอุปกรณ์นี้',
    passkey_pa_not_supported: 'การลงทะเบียนต้องการ Platform Authenticator แต่อุปกรณ์ไม่รองรับ',
    passkey_registration_cancelled: 'การลงทะเบียนพาสคีย์ถูกยกเลิกหรือหมดเวลา',
    passkey_retrieval_cancelled: 'การยืนยันพาสคีย์ถูกยกเลิกหรือหมดเวลา',
    passwordComplexity: {
      maximumLength: 'น้อยกว่า {{length}} ตัวอักษร',
      minimumLength: '{{length}} ตัวอักษรหรือมากกว่า',
      requireLowercase: 'ตัวอักษรพิมพ์เล็ก',
      requireNumbers: 'ตัวเลข',
      requireSpecialCharacter: 'อักขระพิเศษ',
      requireUppercase: 'ตัวอักษรพิมพ์ใหญ่',
      sentencePrefix: 'รหัสผ่านของคุณต้องมี',
    },
    phone_number_exists: undefined,
    protect_check_aborted: undefined,
    protect_check_already_resolved: undefined,
    protect_check_execution_failed: undefined,
    protect_check_invalid_script: undefined,
    protect_check_invalid_sdk_url: undefined,
    protect_check_script_load_failed: undefined,
    protect_check_timed_out: undefined,
    protect_check_unsupported_environment: undefined,
    session_exists: undefined,
    web3_missing_identifier: 'ไม่พบส่วนขยาย Web3 Wallet โปรดติดตั้งเพื่อดำเนินการต่อ',
    web3_signature_request_rejected: 'คุณได้ปฏิเสธคำขอการลงลายเซ็น โปรดลองอีกครั้งเพื่อดำเนินการต่อ',
    web3_solana_signature_generation_failed: 'เกิดข้อผิดพลาดขณะสร้างลายเซ็น โปรดลองอีกครั้งเพื่อดำเนินการต่อ',
    zxcvbn: {
      couldBeStronger: 'รหัสผ่านของคุณใช้ได้ แต่อาจแข็งแกร่งกว่านี้ ลองเพิ่มตัวอักษรเพิ่มเติม',
      goodPassword: 'รหัสผ่านของคุณตรงตามข้อกำหนดที่จำเป็นทั้งหมด',
      notEnough: 'รหัสผ่านของคุณไม่แข็งแกร่งพอ',
      suggestions: {
        allUppercase: 'ใช้ตัวอักษรใหญ่บางส่วน แต่ไม่ใช่ทั้งหมด',
        anotherWord: 'เพิ่มคำที่ไม่ธรรมดามากขึ้น',
        associatedYears: 'หลีกเลี่ยงปีที่เกี่ยวข้องกับคุณ',
        capitalization: 'ใช้ตัวอักษรพิมพ์ใหญ่มากกว่าตัวแรก',
        dates: 'หลีกเลี่ยงวันที่และปีที่เกี่ยวข้องกับคุณ',
        l33t: "หลีกเลี่ยงการแทนที่ตัวอักษรที่คาดเดาได้ เช่น '@' แทน 'a'",
        longerKeyboardPattern: 'ใช้รูปแบบแป้นพิมพ์ที่ยาวขึ้นและเปลี่ยนทิศทางการพิมพ์หลายครั้ง',
        noNeed: 'คุณสามารถสร้างรหัสผ่านที่แข็งแกร่งได้โดยไม่ต้องใช้สัญลักษณ์ ตัวเลข หรือตัวอักษรพิมพ์ใหญ่',
        pwned: 'หากคุณใช้รหัสผ่านนี้ที่อื่น คุณควรเปลี่ยนมัน',
        recentYears: 'หลีกเลี่ยงเลขปีศักราชล่าสุด',
        repeated: 'หลีกเลี่ยงคำและตัวอักษรที่ซ้ำกัน',
        reverseWords: 'หลีกเลี่ยงการสะกดคำธรรมดาแบบย้อนกลับ',
        sequences: 'หลีกเลี่ยงลำดับตัวอักษรธรรมดา',
        useWords: 'ควรใช้หลายคำ แต่หลีกเลี่ยงวลีธรรมดา',
      },
      warnings: {
        common: 'นี่เป็นรหัสผ่านที่ใช้กันทั่วไป',
        commonNames: 'ชื่อและนามสกุลธรรมดาถูกคาดเดาได้ง่าย',
        dates: 'วันที่ถูกคาดเดาได้ง่าย',
        extendedRepeat: 'รูปแบบตัวอักษรที่ซ้ำกัน เช่น "abcabcabc" ถูกคาดเดาได้ง่าย',
        keyPattern: 'รูปแบบการกดคีย์บอร์ดสั้น ๆ ถูกคาดเดาได้ง่าย',
        namesByThemselves: 'ชื่อหรือนามสกุลเดียวถูกคาดเดาได้ง่าย',
        pwned: 'รหัสผ่านของคุณถูกเปิดเผยจากการละเมิดข้อมูลในอินเทอร์เน็ต',
        recentYears: 'ปีล่าสุดถูกคาดเดาได้ง่าย',
        sequences: 'ลำดับตัวอักษรธรรมดา เช่น "abc" ถูกคาดเดาได้ง่าย',
        similarToCommon: 'นี่คล้ายกับรหัสผ่านที่ใช้กันทั่วไป',
        simpleRepeat: 'ตัวอักษรที่ซ้ำกัน เช่น "aaa" ถูกคาดเดาได้ง่าย',
        straightRow: 'แถวตรงของปุ่มบนแป้นพิมพ์ถูกคาดเดาได้ง่าย',
        topHundred: 'นี่เป็นรหัสผ่านที่ใช้บ่อย',
        topTen: 'นี่เป็นรหัสผ่านที่ใช้มาก',
        userInputs: 'ไม่ควรมีข้อมูลส่วนบุคคลหรือข้อมูลที่เกี่ยวข้องกับหน้าเว็บ',
        wordByItself: 'คำเดี่ยว ๆ ถูกคาดเดาได้ง่าย',
      },
    },
  },
  userButton: {
    action__addAccount: 'เพิ่มบัญชี',
    action__closeUserMenu: undefined,
    action__manageAccount: 'จัดการบัญชี',
    action__openUserMenu: undefined,
    action__signOut: 'ออกจากระบบ',
    action__signOutAll: 'ออกจากระบบทุกบัญชี',
    label__accountActions: 'การดำเนินการบัญชี',
    label__activeSessions: 'เซสชันที่ใช้งานอยู่',
    label__userButtonPopover: 'แผงบัญชี',
  },
  userProfile: {
    apiKeysPage: {
      title: 'คีย์ API',
    },
    backupCodePage: {
      actionLabel__copied: 'คัดลอกแล้ว!',
      actionLabel__copy: 'คัดลอกทั้งหมด',
      actionLabel__download: 'ดาวน์โหลด .txt',
      actionLabel__print: 'พิมพ์',
      infoText1: 'รหัสสำรองจะถูกเปิดใช้งานสำหรับบัญชีนี้',
      infoText2:
        'เก็บรหัสสำรองเป็นความลับและเก็บไว้อย่างปลอดภัย คุณสามารถสร้างรหัสสำรองใหม่หากคุณสงสัยว่ามันอาจถูกบุกรุก',
      subtitle__codelist: 'เก็บไว้อย่างปลอดภัยและเก็บเป็นความลับ',
      successMessage:
        'รหัสสำรองเปิดใช้งานแล้ว คุณสามารถใช้หนึ่งในนี้เพื่อเข้าสู่ระบบบัญชีของคุณ หากคุณสูญเสียการเข้าถึงอุปกรณ์ยืนยันตัวตน รหัสแต่ละอันใช้ได้เพียงครั้งเดียว',
      successSubtitle: 'คุณสามารถใช้หนึ่งในนี้เพื่อเข้าสู่ระบบบัญชีของคุณ หากคุณสูญเสียการเข้าถึงอุปกรณ์ยืนยันตัวตน',
      title: 'เพิ่มการยืนยันด้วยรหัสสำรอง',
      title__codelist: 'รหัสสำรอง',
    },
    billingPage: {
      accountCreditsSection: {
        title: undefined,
        viewHistory: undefined,
      },
      creditHistoryPage: {
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
      },
      paymentHistorySection: {
        empty: 'ไม่มีประวัติการชำระเงิน',
        notFound: 'ไม่พบความพยายามในการชำระเงิน',
        tableHeader__amount: 'จำนวนเงิน',
        tableHeader__date: 'วันที่',
        tableHeader__status: 'สถานะ',
      },
      paymentMethodsSection: {
        actionLabel__default: 'ตั้งเป็นค่าเริ่มต้น',
        actionLabel__remove: 'ลบ',
        add: 'เพิ่มวิธีชำระเงินใหม่',
        addSubtitle: 'เพิ่มวิธีการชำระเงินใหม่ในบัญชีของคุณ',
        cancelButton: 'ยกเลิก',
        formButtonPrimary__add: 'เพิ่มวิธีชำระเงิน',
        formButtonPrimary__pay: 'ชำระ {{amount}}',
        payWithTestCardButton: 'ชำระด้วยบัตรทดสอบ',
        removeMethod: {
          messageLine1: '{{identifier}} จะถูกลบออกจากบัญชีนี้',
          messageLine2:
            'คุณจะไม่สามารถใช้แหล่งชำระเงินนี้ได้อีก และการสมัครสมาชิกแบบต่ออายุอัตโนมัติที่เกี่ยวข้องจะไม่ทำงานอีกต่อไป',
          successMessage: '{{paymentMethod}} ได้ถูกลบออกจากบัญชีของคุณแล้ว',
          title: 'ลบวิธีชำระเงิน',
        },
        title: 'วิธีการชำระเงิน',
      },
      start: {
        headerTitle__payments: 'การชำระเงิน',
        headerTitle__plans: 'แผน',
        headerTitle__statements: 'ใบแจ้งหนี้',
        headerTitle__subscriptions: 'การสมัครสมาชิก',
      },
      statementsSection: {
        empty: 'ไม่มีใบแจ้งหนี้ที่จะแสดง',
        itemCaption__paidForPlan: 'ชำระสำหรับแผน {{plan}} {{period}}',
        itemCaption__payerCredit: undefined,
        itemCaption__proratedCredit: 'เครดิตตามสัดส่วนสำหรับการใช้งานบางส่วนของการสมัครสมาชิกก่อนหน้า',
        itemCaption__subscribedAndPaidForPlan: 'สมัครสมาชิกและชำระสำหรับแผน {{plan}} {{period}}',
        notFound: 'ไม่พบใบแจ้งหนี้',
        tableHeader__amount: 'จำนวนเงิน',
        tableHeader__date: 'วันที่',
        title: 'ใบแจ้งหนี้',
        totalPaid: 'รวมที่ชำระ',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: undefined,
        actionLabel__newSubscription: 'สมัครสมาชิกแผน',
        actionLabel__switchPlan: 'เปลี่ยนแผน',
        overview: undefined,
        tableHeader__edit: 'แก้ไข',
        tableHeader__plan: 'แผน',
        tableHeader__startDate: 'วันที่เริ่มต้น',
        title: 'การสมัครสมาชิก',
      },
      subscriptionsSection: {
        actionLabel__default: 'จัดการ',
      },
      switchPlansSection: {
        title: 'เปลี่ยนแผน',
      },
      title: 'การเรียกเก็บเงิน',
    },
    connectedAccountPage: {
      formHint: 'เลือกผู้ให้บริการเพื่อเชื่อมต่อบัญชีของคุณ',
      formHint__noAccounts: 'ไม่มีผู้ให้บริการบัญชีภายนอกที่สามารถใช้ได้',
      removeResource: {
        messageLine1: '{{identifier}} จะถูกลบออกจากบัญชีนี้',
        messageLine2: 'คุณจะไม่สามารถใช้บัญชีที่เชื่อมต่อนี้ได้อีก และฟีเจอร์ที่เกี่ยวข้องจะไม่ทำงานอีกต่อไป',
        successMessage: '{{connectedAccount}} ได้ถูกลบออกจากบัญชีของคุณแล้ว',
        title: 'ลบบัญชีที่เชื่อมต่อ',
      },
      socialButtonsBlockButton: '{{provider|titleize}}',
      successMessage: 'ผู้ให้บริการได้ถูกเพิ่มไปยังบัญชีของคุณ',
      title: 'เพิ่มบัญชีที่เชื่อมต่อ',
    },
    deletePage: {
      actionDescription: 'พิมพ์ "Delete account" ด้านล่างเพื่อดำเนินการต่อ',
      confirm: 'Delete account',
      messageLine1:
        'คุณแน่ใจหรือไม่ว่าต้องการลบบัญชีของคุณ? ข้อมูลที่เกี่ยวข้องบางส่วนอาจถูกเก็บไว้ หากต้องการขอลบข้อมูลทั้งหมด โปรดติดต่อฝ่ายสนับสนุน',
      messageLine2: 'การดำเนินการนี้จะถาวรและไม่สามารถยกเลิกได้',
      title: 'ลบบัญชี',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'อีเมลที่มีรหัสยืนยันจะถูกส่งไปยังที่อยู่อีเมลนี้',
        formSubtitle: 'ใส่รหัสยืนยันที่ส่งไปยัง {{identifier}}',
        formTitle: 'รหัสยืนยัน',
        resendButton: 'ไม่ได้รับรหัส? ส่งใหม่',
        successMessage: 'อีเมล {{identifier}} ได้ถูกเพิ่มไปยังบัญชีของคุณแล้ว',
      },
      emailLink: {
        formHint: 'อีเมลที่มีลิงก์ยืนยันจะถูกส่งไปยังที่อยู่อีเมลนี้',
        formSubtitle: 'คลิกที่ลิงก์ยืนยันในอีเมลที่ส่งไปยัง {{identifier}}',
        formTitle: 'ลิงก์ยืนยัน',
        resendButton: 'ไม่ได้รับลิงก์? ส่งใหม่',
        successMessage: 'อีเมล {{identifier}} ได้ถูกเพิ่มไปยังบัญชีของคุณแล้ว',
      },
      enterpriseSSOLink: {
        formButton: 'คลิกเพื่อเข้าสู่ระบบ',
        formSubtitle: 'ทำการเข้าสู่ระบบด้วย {{identifier}} ให้เสร็จสิ้น',
      },
      formHint: 'คุณจะต้องยืนยันที่อยู่อีเมลนี้ก่อนที่จะสามารถเพิ่มไปยังบัญชีของคุณได้',
      removeResource: {
        messageLine1: '{{identifier}} จะถูกลบออกจากบัญชีนี้',
        messageLine2: 'คุณจะไม่สามารถเข้าสู่ระบบโดยใช้ที่อยู่อีเมลนี้ได้อีก',
        successMessage: '{{emailAddress}} ได้ถูกลบออกจากบัญชีของคุณแล้ว',
        title: 'ลบที่อยู่อีเมล',
      },
      title: 'เพิ่มที่อยู่อีเมล',
      verifyTitle: 'ยืนยันที่อยู่อีเมล',
    },
    formButtonPrimary__add: 'เพิ่ม',
    formButtonPrimary__continue: 'ดำเนินการต่อ',
    formButtonPrimary__finish: 'เสร็จสิ้น',
    formButtonPrimary__remove: 'ลบ',
    formButtonPrimary__save: 'บันทึก',
    formButtonReset: 'ยกเลิก',
    mfaPage: {
      formHint: 'เลือกวิธีที่จะเพิ่ม',
      title: 'เพิ่มการยืนยันตัวตนสองขั้นตอน',
    },
    mfaPhoneCodePage: {
      backButton: 'ใช้เบอร์ที่มีอยู่',
      primaryButton__addPhoneNumber: 'เพิ่มหมายเลขโทรศัพท์',
      removeResource: {
        messageLine1: '{{identifier}} จะไม่รับรหัสยืนยันเมื่อเข้าสู่ระบบอีกต่อไป',
        messageLine2: 'บัญชีของคุณอาจไม่ปลอดภัยเท่าเดิม คุณแน่ใจหรือไม่ว่าต้องการดำเนินการต่อ?',
        successMessage: 'การยืนยันตัวตนสองขั้นตอนด้วย SMS code ถูกลบสำหรับ {{mfaPhoneCode}}',
        title: 'ลบการยืนยันตัวตนสองขั้นตอน',
      },
      subtitle__availablePhoneNumbers:
        'เลือกหมายเลขโทรศัพท์ที่มีอยู่เพื่อลงทะเบียนสำหรับการยืนยันตัวตนสองขั้นตอนด้วย SMS code หรือเพิ่มใหม่',
      subtitle__unavailablePhoneNumbers:
        'ไม่มีหมายเลขโทรศัพท์ที่สามารถใช้ได้เพื่อลงทะเบียนสำหรับการยืนยันตัวตนสองขั้นตอนด้วย SMS code โปรดเพิ่มใหม่',
      successMessage1: 'เมื่อเข้าสู่ระบบ คุณจะต้องใส่รหัสยืนยันที่ส่งไปยังหมายเลขโทรศัพท์นี้เป็นขั้นตอนเพิ่มเติม',
      successMessage2:
        'บันทึกรหัสสำรองเหล่านี้และเก็บไว้ในที่ปลอดภัย หากคุณสูญเสียการเข้าถึงอุปกรณ์ยืนยันตัวตน คุณสามารถใช้รหัสสำรองเพื่อเข้าสู่ระบบได้',
      successTitle: 'เปิดใช้การยืนยันด้วย SMS code แล้ว',
      title: 'เพิ่มการยืนยันด้วย SMS code',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'สแกน QR code แทน',
        buttonUnableToScan__nonPrimary: 'ไม่สามารถสแกน QR code?',
        infoText__ableToScan:
          'ตั้งค่าวิธีเข้าสู่ระบบใหม่ในแอป Authenticator ของคุณและสแกน QR code ด้านล่างเพื่อเชื่อมโยงกับบัญชีของคุณ',
        infoText__unableToScan: 'ตั้งค่าวิธีเข้าสู่ระบบใหม่ใน Authenticator ของคุณและใส่ Key ที่ให้ไว้ด้านล่าง',
        inputLabel__unableToScan1:
          'ตรวจสอบให้แน่ใจว่าได้เปิดใช้ Time-based หรือ One-time passwords แล้ว จากนั้นจึงเชื่อมโยงบัญชีของคุณให้เสร็จสิ้น',
        inputLabel__unableToScan2: 'หรือ หากแอป Authenticator ของคุณรองรับ TOTP URIs คุณยังสามารถคัดลอก URI เต็มได้',
      },
      removeResource: {
        messageLine1: 'รหัสยืนยันจาก Authenticator นี้จะไม่จำเป็นต้องใช้เมื่อเข้าสู่ระบบอีกต่อไป',
        messageLine2: 'บัญชีของคุณอาจไม่ปลอดภัยเท่าเดิม คุณแน่ใจหรือไม่ว่าต้องการดำเนินการต่อ?',
        successMessage: 'การยืนยันตัวตนสองขั้นตอนผ่านแอป Authenticator ถูกลบแล้ว',
        title: 'ลบการยืนยันตัวตนสองขั้นตอน',
      },
      successMessage:
        'การยืนยันตัวตนสองขั้นตอนเปิดใช้งานแล้ว เมื่อเข้าสู่ระบบ คุณจะต้องใส่รหัสยืนยันจาก Authenticator นี้เป็นขั้นตอนเพิ่มเติม',
      title: 'เพิ่มแอป Authenticator',
      verifySubtitle: 'ใส่รหัสยืนยันที่สร้างโดยแอป Authenticator ของคุณ',
      verifyTitle: 'รหัสยืนยัน',
    },
    mobileButton__menu: 'เมนู',
    navbar: {
      account: 'โปรไฟล์',
      apiKeys: 'คีย์ API',
      billing: 'การเรียกเก็บเงิน',
      description: 'จัดการข้อมูลบัญชีของคุณ',
      security: 'ความปลอดภัย',
      title: 'บัญชี',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}} จะถูกลบออกจากบัญชีนี้',
        title: 'ลบพาสคีย์',
      },
      subtitle__rename: 'คุณสามารถเปลี่ยนชื่อพาสคีย์เพื่อให้ค้นหาได้ง่ายขึ้น',
      title__rename: 'เปลี่ยนชื่อพาสคีย์',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions: 'แนะนำให้ออกจากระบบจากอุปกรณ์อื่นทั้งหมดที่อาจใช้รหัสผ่านเก่าของคุณ',
      readonly: 'รหัสผ่านของคุณไม่สามารถแก้ไขได้ในขณะนี้ เนื่องจากคุณสามารถเข้าสู่ระบบผ่านการเชื่อมต่อองค์กรเท่านั้น',
      successMessage__set: 'รหัสผ่านของคุณได้รับการตั้งค่าแล้ว',
      successMessage__signOutOfOtherSessions: 'อุปกรณ์อื่นทั้งหมดได้ออกจากระบบแล้ว',
      successMessage__update: 'รหัสผ่านของคุณได้รับการอัปเดตแล้ว',
      title__set: 'ตั้งรหัสผ่าน',
      title__update: 'อัปเดตรหัสผ่าน',
    },
    phoneNumberPage: {
      infoText: 'ข้อความที่มีรหัสยืนยันจะถูกส่งไปยังหมายเลขโทรศัพท์นี้ อาจมีค่าข้อความและข้อมูล',
      removeResource: {
        messageLine1: '{{identifier}} จะถูกลบออกจากบัญชีนี้',
        messageLine2: 'คุณจะไม่สามารถเข้าสู่ระบบโดยใช้หมายเลขโทรศัพท์นี้ได้อีก',
        successMessage: '{{phoneNumber}} ได้ถูกลบออกจากบัญชีของคุณแล้ว',
        title: 'ลบหมายเลขโทรศัพท์',
      },
      successMessage: '{{identifier}} ได้ถูกเพิ่มไปยังบัญชีของคุณแล้ว',
      title: 'เพิ่มหมายเลขโทรศัพท์',
      verifySubtitle: 'ใส่รหัสยืนยันที่ส่งไปยัง {{identifier}}',
      verifyTitle: 'ยืนยันหมายเลขโทรศัพท์',
    },
    plansPage: {
      title: 'แผน',
    },
    profilePage: {
      fileDropAreaHint: 'ขนาดแนะนำ 1:1 ไม่เกิน 10MB',
      imageFormDestructiveActionSubtitle: 'ลบ',
      imageFormSubtitle: 'อัปโหลด',
      imageFormTitle: 'รูปโปรไฟล์',
      readonly: 'ข้อมูลโปรไฟล์ของคุณได้รับการจัดหาโดยการเชื่อมต่อองค์กรและไม่สามารถแก้ไขได้',
      successMessage: 'โปรไฟล์ของคุณได้รับการอัปเดตแล้ว',
      title: 'อัปเดตโปรไฟล์',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'ออกจากระบบ',
        title: 'อุปกรณ์ที่กำลังใช้งาน',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'เชื่อมต่อใหม่',
        actionLabel__reauthorize: 'อนุญาตตอนนี้',
        destructiveActionTitle: 'ลบ',
        primaryButton: 'เชื่อมต่อบัญชี',
        subtitle__disconnected: 'บัญชีนี้ถูกตัดการเชื่อมต่อแล้ว',
        subtitle__reauthorize:
          'ขอบเขตที่ต้องการได้รับการอัปเดตแล้ว และคุณอาจประสบปัญหาการทำงานที่จำกัด โปรดอนุญาตแอปพลิเคชันนี้ใหม่เพื่อหลีกเลี่ยงปัญหา',
        title: 'บัญชีที่เชื่อมต่อ',
      },
      dangerSection: {
        deleteAccountButton: 'ลบบัญชี',
        title: 'ลบบัญชี',
      },
      emailAddressesSection: {
        destructiveAction: 'ลบอีเมล',
        detailsAction__nonPrimary: 'ตั้งเป็นหลัก',
        detailsAction__primary: 'ทำการยืนยันให้เสร็จสิ้น',
        detailsAction__unverified: 'ยืนยัน',
        primaryButton: 'เพิ่มที่อยู่อีเมล',
        title: 'ที่อยู่อีเมล',
      },
      enterpriseAccountsSection: {
        primaryButton: 'เชื่อมต่อบัญชี',
        title: 'บัญชีองค์กร',
      },
      headerTitle__account: 'รายละเอียดโปรไฟล์',
      headerTitle__security: 'ความปลอดภัย',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'สร้างใหม่',
          headerTitle: 'รหัสสำรอง',
          subtitle__regenerate: 'รับชุดรหัสสำรองใหม่ที่ปลอดภัย รหัสสำรองก่อนหน้าจะถูกลบและไม่สามารถใช้ได้',
          title__regenerate: 'สร้างรหัสสำรองใหม่',
        },
        phoneCode: {
          actionLabel__setDefault: 'ตั้งเป็นค่าเริ่มต้น',
          destructiveActionLabel: 'ลบ',
        },
        primaryButton: 'เพิ่มการยืนยันตัวตนสองขั้นตอน',
        title: 'การยืนยันตัวตนสองขั้นตอน',
        totp: {
          destructiveActionTitle: 'ลบ',
          headerTitle: 'แอป Authenticator',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'ลบ',
        menuAction__rename: 'เปลี่ยนชื่อ',
        primaryButton: 'เพิ่มพาสคีย์',
        title: 'พาสคีย์',
      },
      passwordSection: {
        primaryButton__setPassword: 'ตั้งรหัสผ่าน',
        primaryButton__updatePassword: 'อัปเดตรหัสผ่าน',
        title: 'รหัสผ่าน',
      },
      phoneNumbersSection: {
        destructiveAction: 'ลบหมายเลขโทรศัพท์',
        detailsAction__nonPrimary: 'ตั้งเป็นหลัก',
        detailsAction__primary: 'ทำการยืนยันให้เสร็จสิ้น',
        detailsAction__unverified: 'ยืนยันหมายเลขโทรศัพท์',
        primaryButton: 'เพิ่มหมายเลขโทรศัพท์',
        title: 'หมายเลขโทรศัพท์',
      },
      profileSection: {
        primaryButton: 'อัปเดตโปรไฟล์',
        title: 'โปรไฟล์',
      },
      usernameSection: {
        primaryButton__setUsername: 'ตั้งชื่อผู้ใช้',
        primaryButton__updateUsername: 'อัปเดตชื่อผู้ใช้',
        title: 'ชื่อผู้ใช้',
      },
      web3WalletsSection: {
        destructiveAction: 'ลบวอลเล็ต',
        detailsAction__nonPrimary: 'ตั้งเป็นหลัก',
        primaryButton: 'เชื่อมต่อวอลเล็ต',
        title: 'วอลเล็ต Web3',
        web3SelectSolanaWalletScreen: {
          subtitle: 'เลือกกระเป๋าเงิน Solana เพื่อเชื่อมต่อกับบัญชีของคุณ',
          title: 'เพิ่มกระเป๋าเงิน Solana',
        },
      },
    },
    usernamePage: {
      successMessage: 'ชื่อผู้ใช้ของคุณได้รับการอัปเดตแล้ว',
      title__set: 'ตั้งชื่อผู้ใช้',
      title__update: 'อัปเดตชื่อผู้ใช้',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} จะถูกลบออกจากบัญชีนี้',
        messageLine2: 'คุณจะไม่สามารถเข้าสู่ระบบโดยใช้วอลเล็ต Web3 นี้ได้อีก',
        successMessage: '{{web3Wallet}} ได้ถูกลบออกจากบัญชีของคุณแล้ว',
        title: 'ลบวอลเล็ต Web3',
      },
      subtitle__availableWallets: 'เลือกวอลเล็ต Web3 เพื่อเชื่อมต่อกับบัญชีของคุณ',
      subtitle__unavailableWallets: 'ไม่มีวอลเล็ต Web3 ที่สามารถใช้ได้',
      successMessage: 'วอลเล็ตได้ถูกเพิ่มไปยังบัญชีของคุณแล้ว',
      title: 'เพิ่มวอลเล็ต Web3',
      web3WalletButtonsBlockButton: '{{provider|titleize}}',
    },
  },
  waitlist: {
    start: {
      actionLink: 'เข้าสู่ระบบ',
      actionText: 'มีสิทธิ์เข้าถึงแล้ว?',
      formButton: 'เข้าร่วม Waitlist',
      subtitle: 'ใส่ที่อยู่อีเมลของคุณ แล้วเราจะแจ้งให้คุณทราบเมื่อคิวของคุณพร้อม',
      title: 'เข้าร่วม Waitlist',
    },
    success: {
      message: 'คุณจะถูกเปลี่ยนเส้นทางในไม่ช้า...',
      subtitle: 'เราจะติดต่อเมื่อคิวของคุณพร้อม',
      title: 'ขอบคุณที่เข้าร่วม Waitlist!',
    },
  },
  web3SolanaWalletButtons: {
    connect: 'เชื่อมต่อด้วย {{walletName}}',
    continue: 'ดำเนินการต่อด้วย {{walletName}}',
    noneAvailable:
      'ไม่พบกระเป๋าเงิน Solana Web3 โปรดติดตั้ง {{ solanaWalletsLink || link("wallet extension") }} ที่รองรับ Web3',
  },
} as const;
